import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { BehaviorSubject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    ApiThesaurusConcept,
    apiThesaurusConceptToReference,
    FormValue,
    FormValueState,
    ITreeViewerNode,
    MultiFormSelectOptions,
    ShFieldDefinitionService,
    ThesaurusConceptReference,
    TreeViewerNode
} from '@stylehaven/components';
import { ThesaurusService } from '../../../common/services/thesaurus/thesaurus.service';
import { ThesaurusTreeViewContainerComponent } from '../../containers/thesaurus-tree-view-container/thesaurus-tree-view-container.component';

@Component({
    standalone: false,
    selector: 'med-thesaurus-tree-single',
    templateUrl: './thesaurus-tree-single.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThesaurusTreeSingleComponent
    extends ThesaurusTreeViewContainerComponent
    implements OnInit, OnDestroy, AfterViewInit
{
    public tags$: BehaviorSubject<MultiFormSelectOptions<ApiThesaurusConcept>> = new BehaviorSubject<
        MultiFormSelectOptions<ApiThesaurusConcept>
    >({ options: [], isLiveData: false });

    public synchronizeConceptSelection$: BehaviorSubject<ThesaurusConceptReference | null> =
        new BehaviorSubject<ThesaurusConceptReference | null>(null);

    public thesaurusSearchControl: UntypedFormControl = new UntypedFormControl();
    public selectedNode: ITreeViewerNode<ApiThesaurusConcept> | undefined;

    @Input() autoFocus: boolean = false;

    @Input() initialValue: ThesaurusConceptReference | null;

    @Output() concept: EventEmitter<ThesaurusConceptReference | null> =
        new EventEmitter<ThesaurusConceptReference | null>();

    constructor(
        protected thesaurusService: ThesaurusService,
        protected fieldDefinitionService: ShFieldDefinitionService,
        protected translateService: SharedLanguageService
    ) {
        super(thesaurusService, fieldDefinitionService, translateService);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.tags$.complete();
    }

    ngAfterViewInit(): void {
        this.syncInitialValue();
    }

    private syncInitialValue(): void {
        this.synchronizeConceptSelection$.next(this.initialValue);
        if (this.initialValue) {
            this.activateNode(this.initialValue);
        }
    }

    protected initSuggestionLimit(): void {
        this.suggestionLimit$ = this.suggestions$.pipe(map(suggestions => suggestions.length === 1));
    }

    protected initEmissionStream(): void {
        this.thesaurusSearchControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value: FormValue<ThesaurusConceptReference>[]) => {
                this.concept.next(
                    value
                        .filter(formValue => formValue.state === FormValueState.SET)
                        .map(formValue => formValue.value)[0] ?? null
                );
            });
    }

    protected isActive(thesaurusConcept: ApiThesaurusConcept): boolean {
        if (!this.currentSelectedConcept) return false;
        return this.currentSelectedConcept.value.Uri === thesaurusConcept.Uri;
    }

    public onUpdatedNodes(nodes: ITreeViewerNode<ApiThesaurusConcept>[]) {
        this.currentNodes = nodes;
    }

    public onSelectedNodesChange(nodes: ITreeViewerNode<ApiThesaurusConcept>[]): void {
        const concepts: ApiThesaurusConcept[] = nodes.filter(node => !node.disabled).map(node => node.data);

        if (concepts.length !== 1) {
            this.synchronizeConceptSelection$.next(null);
            return;
        }

        this.getFullBreadcrumbs(concepts)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(fullConcepts => {
                const currentlySet: FormValue<ThesaurusConceptReference> | undefined = this.currentSelectedConcept;

                if (fullConcepts[0].Uri === currentlySet?.value.Uri) {
                    return;
                }

                this.toggleConceptNodeState(fullConcepts[0], true);

                this.concept.next(apiThesaurusConceptToReference(fullConcepts[0]));
                this.synchronizeConceptSelection$.next(apiThesaurusConceptToReference(fullConcepts[0]));
            });
    }

    public onLookupSelection(concept: ThesaurusConceptReference | null): void {
        // always deactivate previous
        this.deactivateNode();

        if (concept) this.activateNode(concept);
        if (concept?.Uri !== this.synchronizeConceptSelection$.value?.Uri) {
            this.synchronizeConceptSelection$.next(concept);
        }

        this.concept.next(concept);
    }

    private activateNode(concept: ThesaurusConceptReference): void {
        const nodeToActivate: TreeViewerNode<ApiThesaurusConcept> | undefined = this.findTreeNodeById(
            this.currentNodes,
            concept?.Uri
        ) as TreeViewerNode<ApiThesaurusConcept> | undefined;

        // An update is neccessary
        if (nodeToActivate?.isActive === false) {
            this.toggleConceptNodeState(nodeToActivate.data ?? null, true);
            this.nodesUpdated$.next(true);
            this.concept.next(concept);
        }
    }

    private deactivateNode(): void {
        if (this.selectedNode?.isActive) {
            this.toggleConceptNodeState(this.selectedNode.data, false);
            this.nodesUpdated$.next(true);
        }
    }

    protected toggleConceptNodeState(concept: ApiThesaurusConcept | null, state: boolean): boolean {
        const nodeToActivate: TreeViewerNode<ApiThesaurusConcept> | undefined = this.findTreeNodeById(
            this.currentNodes,
            concept?.Uri as string
        ) as TreeViewerNode<ApiThesaurusConcept> | undefined;

        if (!nodeToActivate || nodeToActivate.isActive === state) {
            this.selectedNode = undefined;
            return false;
        }

        nodeToActivate.isActive = state;

        if (state) this.selectedNode = nodeToActivate as ITreeViewerNode<ApiThesaurusConcept>;
        else this.selectedNode = undefined;
        return true;
    }

    private get currentSelectedConcept(): FormValue<ThesaurusConceptReference> | undefined {
        return this.thesaurusSearchControl.value?.find(
            (formValue: FormValue<ThesaurusConceptReference>) => formValue.state === FormValueState.SET
        );
    }
}
